/**
 * CollectPaymentModal — PAY-02 (2026-08-06). The office's one payment surface:
 * charge the card on file, key a new card (Stripe-hosted fields — numbers never
 * touch Pegasus), or record a check/cash payment, allocated across one or more
 * open Pegasus invoices (a landlord can pay three properties' invoices at once).
 *
 * Surcharge rules preview here, ENFORCE server-side. NOTE (GM 2026-08-07):
 * surcharge_pct is 0 — no fee UI renders; the machinery stays for the dial.
 *
 * PAY-03 (2026-08-07): PayLinkRow — the customer can pay THEMSELVES. Copy their
 * secure portal pay link (Billing tab deep link), or send it by email/text
 * through the existing comms-gated senders (nothing sends until comms_live).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CreditCard, Banknote, Landmark, Phone, ShieldCheck, CheckCircle2, Copy, Check, Send, MessageSquare, Link2 } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { useEntryFormKeys } from "@/lib/keys";
import { supabase } from "@/lib/supabase";
import {
  loadStripeJs, moneyExact, cardBrandLabel, usePaymentConfig, useCustomerPaymentProfile,
  useChargeNewCard, useChargeSavedCard, useRecordManualPayment, collectPayment,
  type StripeCardElement, type StripeJs,
} from "./payments";

export interface OpenInvoice {
  id: string;
  number: string;
  date: string | null;
  label: string;
  total: number;
  balance: number;
  /** AR-360: set = an ESC-mirror invoice; the allocation is recorded by this
   *  invoice NUMBER (esc_invoice_no) instead of a Pegasus invoice id. */
  escNo?: string | null;
  /** AR-360: the property's address label — the Verde "which house is this
   *  invoice for" read, shown on multi-property accounts. */
  property?: string | null;
}

type Method = "saved" | "new_card" | "manual";

export function CollectPaymentModal({
  custno, customerName, invoices, onClose, initialInvoiceId, preselectAll,
}: {
  custno: string;
  customerName?: string | null;
  invoices: OpenInvoice[];
  onClose: () => void;
  /** Preselect exactly this invoice (row-level / document-level entry points). */
  initialInvoiceId?: string | null;
  /** AR-360 selection-bar entry: every passed invoice starts checked. */
  preselectAll?: boolean;
}) {
  const { data: config } = usePaymentConfig();
  const { data: profile } = useCustomerPaymentProfile(custno);
  const chargeSaved = useChargeSavedCard(custno);
  const chargeNew = useChargeNewCard(custno);
  const recordManual = useRecordManualPayment(custno);

  // ----- allocations: pick invoices + amounts (default = full balance) -----
  const [selected, setSelected] = useState<Record<string, boolean>>(
    () => Object.fromEntries(invoices.map((i, idx) => [i.id, preselectAll ? true : initialInvoiceId ? i.id === initialInvoiceId : idx === 0])),
  );
  const [amounts, setAmounts] = useState<Record<string, string>>(
    () => Object.fromEntries(invoices.map((i) => [i.id, i.balance.toFixed(2)])),
  );
  const allocations = useMemo(
    () =>
      invoices
        .filter((i) => selected[i.id])
        .map((i) => ({
          invoice_id: i.escNo ? null : i.id,
          esc_invoice_no: i.escNo ?? null,
          amount: Number(amounts[i.id] || 0),
        }))
        .filter((a) => a.amount > 0),
    [invoices, selected, amounts],
  );
  const anyEsc = invoices.some((i) => selected[i.id] && i.escNo);
  const amount = allocations.reduce((s, a) => s + a.amount, 0);

  // ----- method -----
  const hasSaved = !!profile?.default_payment_method_id;
  const [method, setMethod] = useState<Method>(hasSaved ? "saved" : "new_card");
  useEffect(() => { if (hasSaved) setMethod("saved"); }, [hasSaved]);
  const [manualKind, setManualKind] = useState<"check" | "cash">("check");
  const [reference, setReference] = useState("");
  const [saveCard, setSaveCard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ total: number; label: string } | null>(null);

  // ----- surcharge preview (server is the enforcer; this mirrors for honesty) -----
  const bigInvoice = useMemo(
    () => invoices.some((i) => selected[i.id] && i.total >= (config?.surcharge_min_invoice ?? Infinity)),
    [invoices, selected, config],
  );
  const pct = config?.surcharge_pct ?? 0;
  const previewSurcharge = (funding: string | null | undefined) =>
    bigInvoice && funding === "credit" && pct > 0 ? Math.round(amount * pct) / 100 : 0;

  // ----- Stripe-hosted card fields (new card) -----
  const cardHost = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<StripeJs | null>(null);
  const elementRef = useRef<StripeCardElement | null>(null);
  const [cardReady, setCardReady] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  // priced step for a NEW card: we learn credit-vs-debit from the tokenized card,
  // then show the exact total (incl. any fee) before anything is charged.
  const [pending, setPending] = useState<{
    payment_method_id: string; funding: string; surcharge: number; total: number; brand: string | null; last4: string | null;
  } | null>(null);

  useEffect(() => {
    if (method !== "new_card" || !config?.publishable_key || pending) return;
    let cancelled = false;
    loadStripeJs(config.publishable_key).then((stripe) => {
      if (cancelled || !stripe || !cardHost.current) return;
      stripeRef.current = stripe;
      const el = stripe.elements().create("card", { hidePostalCode: false });
      el.mount(cardHost.current);
      el.on("change", (e) => { setCardComplete(!!e.complete); setError(e.error?.message ?? null); });
      elementRef.current = el;
      setCardReady(true);
    });
    return () => { cancelled = true; try { elementRef.current?.unmount(); } catch { /* fine */ } elementRef.current = null; setCardReady(false); setCardComplete(false); };
  }, [method, config?.publishable_key, pending]);

  const busy = chargeSaved.isPending || chargeNew.isPending || recordManual.isPending;
  const disclosure = config?.surcharge_disclosure ?? "";

  // ----- actions -----
  async function priceNewCard() {
    setError(null);
    if (!stripeRef.current || !elementRef.current) return;
    const res = await stripeRef.current.createPaymentMethod({ type: "card", card: elementRef.current });
    if (res.error || !res.paymentMethod) { setError(res.error?.message ?? "Card couldn't be read."); return; }
    try {
      const q = await collectPayment<{ funding: string; surcharge: number; total: number; card_brand: string | null; card_last4: string | null }>({
        action: "quote_new_card", payment_method_id: res.paymentMethod.id, amount, allocations,
      });
      setPending({ payment_method_id: res.paymentMethod.id, funding: q.funding, surcharge: q.surcharge, total: q.total, brand: q.card_brand, last4: q.card_last4 });
    } catch (e) { setError((e as Error).message); }
  }

  async function confirmNewCard() {
    if (!pending) return;
    setError(null);
    try {
      await chargeNew.mutateAsync({
        customer_name: customerName ?? null, amount, allocations,
        payment_method_id: pending.payment_method_id, save_card: saveCard,
        consent_text: saveCard ? "Customer authorized Sedgwick Heating to keep this card on file for future balances." : undefined,
      });
      setDone({ total: pending.total, label: `${cardBrandLabel(pending.brand)} ····${pending.last4 ?? ""}` });
    } catch (e) { setError((e as Error).message); }
  }

  async function confirmSaved() {
    setError(null);
    try {
      const sur = previewSurcharge(profile?.card_funding);
      await chargeSaved.mutateAsync({ amount, allocations });
      setDone({ total: amount + sur, label: `${cardBrandLabel(profile?.card_brand)} ····${profile?.card_last4 ?? ""} on file` });
    } catch (e) { setError((e as Error).message); }
  }

  async function confirmManual() {
    setError(null);
    try {
      await recordManual.mutateAsync({ amount, method: manualKind, reference: reference || undefined, allocations });
      setDone({ total: amount, label: manualKind === "check" ? `check${reference ? ` #${reference}` : ""}` : "cash" });
    } catch (e) { setError((e as Error).message); }
  }

  // KEYS-01b: Enter walks the fields; there is deliberately NO keyboard shortcut
  // that charges a card — a payment is always a click on its button.
  const onFormKeys = useEntryFormKeys();

  // ----- render -----
  const fieldCls = "w-full rounded-md border border-hairline bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30";
  const tabCls = (on: boolean) =>
    cn("flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors",
      on ? "border-brand-300 bg-brand-50 text-brand-700" : "border-hairline text-slate-600 hover:bg-slate-50");

  return (
    <Modal open onClose={() => !busy && onClose()} size="lg" title="Collect Payment"
      subtitle={<span>{customerName ?? custno}{config?.test_mode ? " · TEST MODE — no real charge" : ""}</span>}>
      {done ? (
        <div className="space-y-4 py-6 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
          <p className="text-lg font-semibold text-slate-900">{moneyExact(done.total)} collected</p>
          <p className="text-sm text-slate-500">Paid by {done.label}. It&apos;s on the ledger and the invoice balance updated.</p>
          <Button onClick={onClose}>Done</Button>
        </div>
      ) : (
        <div className="space-y-5" data-entry-form onKeyDown={onFormKeys}>
          {/* APPLY TO */}
          <section>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Apply to</div>
            {invoices.length === 0 ? (
              <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-500">No open invoices with a balance for this customer.</p>
            ) : (
              <ul className="divide-y divide-hairline rounded-lg border border-hairline">
                {invoices.map((inv) => (
                  <li key={inv.id} className="flex items-center gap-3 px-3 py-2">
                    <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={!!selected[inv.id]}
                      onChange={(e) => setSelected((s) => ({ ...s, [inv.id]: e.target.checked }))} />
                    <span className="w-20 shrink-0 font-mono text-xs text-slate-600">#{inv.number.replace(/^0+/, "")}</span>
                    <span className="min-w-0 flex-1 truncate text-xs text-slate-500">
                      {inv.property && <span className="font-medium text-slate-700">{inv.property} · </span>}
                      {inv.label}
                    </span>
                    <span className="shrink-0 text-[11px] tabular-nums text-slate-400">bal {moneyExact(inv.balance)}</span>
                    <div className="relative w-28 shrink-0">
                      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                      <input type="number" min="0" step="0.01" value={amounts[inv.id] ?? ""} disabled={!selected[inv.id]}
                        onChange={(e) => setAmounts((a) => ({ ...a, [inv.id]: e.target.value }))}
                        className="w-full rounded-md border border-hairline bg-white py-1.5 pl-5 pr-2 text-right text-sm tabular-nums disabled:bg-slate-50 disabled:text-slate-300" />
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {anyEsc && (
              <p className="mt-1.5 flex items-center gap-1.5 rounded-md bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-800">
                <span>ESC invoice selected: <b>also post this payment in ESC</b> until cutover.</span>
                <InfoTip label="Why post it in ESC too">The payment goes on the Pegasus ledger and shows on those invoices right away. The ESC figures in Pegasus refresh overnight.</InfoTip>
              </p>
            )}
          </section>

          {/* METHOD */}
          <section>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Payment method</div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={tabCls(method === "saved")} disabled={!hasSaved}
                onClick={() => { setMethod("saved"); setPending(null); }}
                title={hasSaved ? undefined : "No card on file for this customer yet"}>
                <ShieldCheck className="h-3.5 w-3.5" />
                {hasSaved ? `${cardBrandLabel(profile?.card_brand)} ····${profile?.card_last4}` : "Card on file"}
              </button>
              <button type="button" className={tabCls(method === "new_card")} onClick={() => setMethod("new_card")}>
                <CreditCard className="h-3.5 w-3.5" /> New card
              </button>
              <button type="button" className={tabCls(method === "manual")} onClick={() => { setMethod("manual"); setPending(null); }}>
                <Banknote className="h-3.5 w-3.5" /> Check · Cash
              </button>
            </div>
          </section>

          {/* method body */}
          {method === "saved" && hasSaved && (
            <section className="space-y-3 rounded-lg border border-hairline bg-slate-50/60 p-3">
              <p className="text-sm text-slate-600">
                Charge the {cardBrandLabel(profile?.card_brand)} card ending {profile?.card_last4}
                {profile?.card_funding ? ` (${profile.card_funding})` : ""} for{" "}
                <b className="tabular-nums text-slate-900">{moneyExact(amount)}</b>
                {previewSurcharge(profile?.card_funding) > 0 && (
                  <> + <b className="tabular-nums">{moneyExact(previewSurcharge(profile?.card_funding))}</b> processing fee
                    = <b className="tabular-nums text-slate-900">{moneyExact(amount + previewSurcharge(profile?.card_funding))}</b></>
                )}.
              </p>
              {previewSurcharge(profile?.card_funding) > 0 && <Disclosure text={disclosure} />}
              <PhoneReminder show={previewSurcharge(profile?.card_funding) > 0} />
            </section>
          )}

          {method === "new_card" && (
            <section className="space-y-3 rounded-lg border border-hairline bg-slate-50/60 p-3">
              {!config?.configured ? (
                <p className="text-sm text-amber-700">Stripe isn&apos;t connected yet — card entry unlocks when the setup checklist is done. Check &amp; cash recording works now.</p>
              ) : pending ? (
                <>
                  <p className="text-sm text-slate-600">
                    {cardBrandLabel(pending.brand)} ····{pending.last4} · {pending.funding === "credit" ? "credit card" : pending.funding === "debit" ? "debit card" : "card"}.{" "}
                    <b className="tabular-nums text-slate-900">{moneyExact(amount)}</b>
                    {pending.surcharge > 0 && (
                      <> + <b className="tabular-nums">{moneyExact(pending.surcharge)}</b> processing fee
                        = <b className="tabular-nums text-slate-900">{moneyExact(pending.total)}</b></>
                    )}
                  </p>
                  {pending.surcharge > 0 && <Disclosure text={disclosure} />}
                  <PhoneReminder show={pending.surcharge > 0} />
                </>
              ) : (
                <>
                  <div ref={cardHost} className="rounded-md border border-hairline bg-white px-3 py-2.5" />
                  <label className="flex items-start gap-2 text-xs text-slate-600">
                    <input type="checkbox" className="mt-0.5 h-3.5 w-3.5 accent-brand-600" checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} />
                    <span>Keep this card on file for future balances <span className="text-slate-400">(customer said yes — consent is recorded)</span></span>
                  </label>
                  {!cardReady && <p className="text-xs text-slate-400">Loading secure card fields…</p>}
                </>
              )}
            </section>
          )}

          {method === "manual" && (
            <section className="flex flex-wrap items-center gap-2 rounded-lg border border-hairline bg-slate-50/60 p-3">
              <button type="button" className={tabCls(manualKind === "check")} onClick={() => setManualKind("check")}>
                <Landmark className="h-3.5 w-3.5" /> Check
              </button>
              <button type="button" className={tabCls(manualKind === "cash")} onClick={() => setManualKind("cash")}>
                <Banknote className="h-3.5 w-3.5" /> Cash
              </button>
              {manualKind === "check" && (
                <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Check #" className={cn(fieldCls, "w-32")} />
              )}
            </section>
          )}

          {/* PAY-03: self-service — the customer pays from their own phone. */}
          <PayLinkRow custno={custno} />

          {error && <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{error}</p>}

          {/* footer actions */}
          <div className="flex items-center justify-between border-t border-hairline pt-3">
            <span className="text-sm text-slate-500">
              Collecting <b className="tabular-nums text-slate-900">{moneyExact(amount)}</b>
              {allocations.length > 1 ? ` across ${allocations.length} invoices` : ""}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
              {method === "saved" && hasSaved && (
                <Button onClick={confirmSaved} disabled={busy || amount <= 0}>
                  {chargeSaved.isPending ? "Charging…" : `Charge ${moneyExact(amount + previewSurcharge(profile?.card_funding))}`}
                </Button>
              )}
              {method === "new_card" && config?.configured && (pending ? (
                <Button onClick={confirmNewCard} disabled={busy}>
                  {chargeNew.isPending ? "Charging…" : `Charge ${moneyExact(pending.total)}`}
                </Button>
              ) : (
                <Button onClick={priceNewCard} disabled={busy || amount <= 0 || !cardComplete}>Continue</Button>
              ))}
              {method === "manual" && (
                <Button onClick={confirmManual} disabled={busy || amount <= 0}>
                  {recordManual.isPending ? "Recording…" : `Record ${moneyExact(amount)}`}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * "Or let them pay online" — the customer's secure portal pay link (their
 * passwordless portal, Billing tab). Copy always works; Email/Text ride the
 * existing comms-gated senders (send-email / podium-send), so until comms_live
 * flips they respond with the calm "in testing" note instead of sending.
 */
function PayLinkRow({ custno }: { custno: string }) {
  const { data } = useQuery({
    queryKey: ["pay_link", custno],
    queryFn: async () => {
      const [tok, setting, loc, cust] = await Promise.all([
        supabase.rpc("get_customer_link_token", { p_custno: custno }),
        supabase.from("org_setting").select("value").eq("key", "public_app_url").maybeSingle(),
        supabase.from("mirror_location").select("email").eq("custno", custno).order("locno").limit(6),
        supabase.from("mirror_customer").select("phone1").eq("custno", custno).maybeSingle(),
      ]);
      const base = (typeof setting.data?.value === "string" ? setting.data.value : "https://my.sedgwickheating.com").replace(/\/+$/, "");
      const emails = [...new Set(
        ((loc.data ?? []) as { email: string | null }[])
          .map((r) => (r.email ?? "").trim().toLowerCase())
          .filter((e) => e.includes("@")),
      )];
      const phone = ((cust.data as { phone1?: string | null } | null)?.phone1 ?? "").trim() || null;
      return { link: `${base}/portal?t=${tok.data ?? ""}&tab=billing`, emails, phone };
    },
  });
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState<"email" | "text" | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  if (!data) return null;
  const btn = "inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40";

  const copy = () => {
    navigator.clipboard?.writeText(data.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const gatedOrOk = (r: { ok?: boolean; error?: string; blocked?: string } | null, err: unknown, sentMsg: string) => {
    if (r?.blocked === "testing" || /testing|paused/i.test(r?.error ?? "")) {
      setMsg({ ok: true, text: "Comms are in testing — nothing sent yet. Goes live at rollout." });
    } else if (err || !r?.ok) {
      setMsg({ ok: false, text: r?.error ?? "Couldn't send — copy the link instead." });
    } else {
      setMsg({ ok: true, text: sentMsg });
    }
  };
  const emailIt = async () => {
    setBusy("email"); setMsg(null);
    const { data: sd, error } = await supabase.functions.invoke("send-email", {
      body: { to: data.emails, subject: "Pay your Sedgwick Heating balance online", html: payLinkEmail(data.link), custno, message_type: "payment-link" },
    });
    setBusy(null);
    gatedOrOk(sd as { ok?: boolean; error?: string; blocked?: string } | null, error, `Sent to ${data.emails.length === 1 ? data.emails[0] : `${data.emails.length} addresses`}`);
  };
  const textIt = async () => {
    if (!data.phone) return;
    setBusy("text"); setMsg(null);
    const { data: sd, error } = await supabase.functions.invoke("podium-send", {
      body: { to: data.phone, body: `Sedgwick Heating: you can pay your balance securely online here: ${data.link}`, custno },
    });
    setBusy(null);
    gatedOrOk(sd as { ok?: boolean; error?: string; blocked?: string } | null, error, `Texted to ${data.phone}`);
  };

  return (
    <section className="rounded-lg border border-dashed border-hairline bg-slate-50/40 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-0.5 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <Link2 className="h-3.5 w-3.5 text-slate-400" /> Or let them pay online
          <InfoTip label="About the pay link">Their secure portal link, opened straight to Billing. They pay by bank or card from their own phone, with no password.</InfoTip>
        </span>
        <button type="button" onClick={copy} className={btn}>
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy pay link"}
        </button>
        <button type="button" onClick={emailIt} disabled={busy !== null || data.emails.length === 0} className={btn}
          title={data.emails.length === 0 ? "No email on file — copy the link instead" : `Email it to ${data.emails.join(", ")}`}>
          <Send className="h-3.5 w-3.5" /> {busy === "email" ? "Sending…" : "Email it"}
        </button>
        <button type="button" onClick={textIt} disabled={busy !== null || !data.phone} className={btn}
          title={data.phone ? `Text it to ${data.phone}` : "No phone on file"}>
          <MessageSquare className="h-3.5 w-3.5" /> {busy === "text" ? "Sending…" : "Text it"}
        </button>
        {msg && <span className={cn("text-[11px] font-medium", msg.ok ? "text-emerald-600" : "text-amber-600")}>{msg.text}</span>}
      </div>
    </section>
  );
}

// The branded "pay online" email (matches the portal-link template family).
function payLinkEmail(link: string) {
  const LOGO = "https://trfusfityhwecvglnfhm.supabase.co/storage/v1/object/public/email-assets/sedgwick-logo.png";
  return `<div style="max-width:480px;margin:0 auto;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;background:#ffffff;border-radius:14px;overflow:hidden;border:1px solid #e8eaed">
    <div style="text-align:center;padding:26px 24px 20px;border-bottom:3px solid #16314f"><img src="${LOGO}" alt="Sedgwick Heating" style="width:180px;max-width:60%"/></div>
    <div style="padding:30px 34px">
      <h1 style="font-size:20px;color:#16314f;margin:0 0 12px">Pay your balance online</h1>
      <p style="color:#5f6b7a;font-size:15px;line-height:1.6;margin:0 0 22px">Use your secure link to pay straight from your bank or with a card &mdash; it takes about a minute, no password needed.</p>
      <div style="text-align:center"><a href="${link}" style="display:inline-block;background:#c0392b;color:#fff;text-decoration:none;padding:13px 32px;border-radius:9px;font-weight:700;font-size:15px">Pay my balance</a></div>
      <p style="color:#9aa4b1;font-size:13px;line-height:1.6;margin:24px 0 0">This link is just for you &mdash; please don't forward it. Questions? Call <a href="tel:9528819000" style="color:#1f5fa8;font-weight:700">(952) 881-9000</a>.</p>
    </div>
    <div style="text-align:center;font-size:12px;color:#9aa4b1;padding:0 0 22px">Sedgwick Heating &amp; Air Conditioning &middot; Eagan, MN</div>
  </div>`;
}

function Disclosure({ text }: { text: string }) {
  if (!text) return null;
  return <p className="rounded-md bg-white px-3 py-2 text-xs leading-relaxed text-slate-500">{text}</p>;
}
function PhoneReminder({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <p className="flex items-start gap-1.5 text-[11px] text-amber-700">
      <Phone className="mt-0.5 h-3 w-3 shrink-0" />
      <span>Phone payment? Read the processing-fee line to the customer before charging — Minnesota requires saying it out loud.</span>
    </p>
  );
}
